import React, { useState } from 'react';
import {
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { notAFitService } from '../services/notAFitService';

// FEAT-042 — "Not a Fit" silent bidirectional hide (MVP-8.8, MVP-18.16).
// Overflow menu entry on profile pages. The one-line description must
// reassure the marker that the other party is NOT notified — that warm,
// non-confrontational framing is the whole reason we prefer this over a
// hard block.

interface NotAFitMenuItemProps {
  targetUserId: string;
  onCloseMenu?: () => void;
  onMarked?: () => void;
}

export const NotAFitMenuItem: React.FC<NotAFitMenuItemProps> = ({
  targetUserId,
  onCloseMenu,
  onMarked,
}) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (!submitting) {
      setOpen(false);
      onCloseMenu?.();
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await notAFitService.markNotAFit(targetUserId);
      setOpen(false);
      onCloseMenu?.();
      onMarked?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MenuItem onClick={handleOpen} data-testid="not-a-fit-menu-item">
        <ListItemIcon>
          <VisibilityOffIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary="Not a fit (hide quietly)"
          secondary="They won't be notified."
        />
      </MenuItem>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="not-a-fit-dialog-title"
        data-testid="not-a-fit-confirm-dialog"
      >
        <DialogTitle id="not-a-fit-dialog-title">Mark as Not a Fit?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This person will quietly disappear from your search, discovery, and
            new conversations. They will not be notified and won&apos;t see any
            change on their side. You can undo this anytime from Settings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            variant="contained"
            data-testid="not-a-fit-confirm-button"
          >
            {submitting ? 'Hiding…' : 'Hide quietly'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotAFitMenuItem;
